/**
 * Aus Geometrie werden sichtbare Gebaeude: Materialien, Texturen, Schatten.
 * Die Geometrie selbst kommt unveraendert aus `baustile.ts` - hier wird nichts
 * mehr verschoben oder skaliert, sonst waere die gepruefte Grundflaeche wertlos.
 */
import * as THREE from 'three'
import type { HotelId } from '../core/data/hotels'
import { GELAENDE } from '../core/data/spielplan'
import type { Grundriss } from '../core/geometrie'
import { BAUSTILE, bauhoehe, gebaeudeTeile, parkTeile } from './baustile'
import { EBENE } from './ebenen'
import { kacheln } from './brett'
import { dachhaut, fassade, rasen } from './texturen'

/** Setzt eine Gruppe exakt auf den Ankerpunkt ihres Bauplatzes. */
function aufBauplatz(gruppe: THREE.Group, riss: Grundriss): THREE.Group {
  gruppe.position.set(riss.mitte.x, EBENE.gelaende, riss.mitte.z)
  gruppe.rotation.y = -riss.drehung
  return gruppe
}

/** Ein einzelnes Gebaeude einer Ausbaustufe. */
export function baueGebaeude(hotel: HotelId, stufe: number): THREE.Group {
  const riss = GELAENDE[hotel].bauplaetze[stufe]
  if (!riss) throw new Error(`${hotel} hat keine Stufe ${stufe}`)
  const stil = BAUSTILE[hotel]
  const { koerper, dach } = gebaeudeTeile(hotel, stufe, riss)

  // Fensterraster aus den echten Massen: etwa alle 9 mm eine Achse, alle 6 mm ein
  // Geschoss. So bekommt ein Turm viele feine Fenster und ein Bungalow wenige grosse,
  // ohne dass irgendwo eine Zahl von Hand nachgepflegt werden muss.
  const wandhoehe = bauhoehe(hotel, stufe) * stil.koerperAnteil
  const spalten = Math.max(3, Math.min(16, Math.round(riss.breite / 9)))
  const reihen = Math.max(1, Math.min(18, Math.round(wandhoehe / 6)))
  const wandtextur = fassade(stil.wand, stil.fenster, spalten, reihen)

  const wand = new THREE.Mesh(
    koerper,
    new THREE.MeshStandardMaterial({ map: wandtextur, roughness: 0.72, metalness: 0.04 }),
  )
  const dachMesh = new THREE.Mesh(
    dach,
    new THREE.MeshStandardMaterial({
      map: kacheln(dachhaut(stil.dach, stil.dachDunkel), 2, 2),
      roughness: 0.85,
    }),
  )

  const gruppe = new THREE.Group()
  gruppe.name = `${hotel}-${stufe}`
  for (const m of [wand, dachMesh]) {
    m.castShadow = true
    m.receiveShadow = true
    gruppe.add(m)
  }
  return aufBauplatz(gruppe, riss)
}

/** Der Freizeitpark eines Hotels. */
export function baueFreizeitpark(hotel: HotelId): THREE.Group {
  const riss = GELAENDE[hotel].freizeitpark
  const stil = BAUSTILE[hotel]
  const teile = parkTeile(riss)

  const boden = new THREE.Mesh(
    teile.rasen,
    new THREE.MeshStandardMaterial({
      map: kacheln(rasen(stil.park[0], stil.park[1]), 3, 3),
      roughness: 1,
    }),
  )
  const wasser = new THREE.Mesh(
    teile.wasser,
    new THREE.MeshStandardMaterial({
      color: stil.park[2],
      roughness: 0.18,
      metalness: 0.1,
    }),
  )
  const kronen = new THREE.Mesh(
    teile.baeume,
    new THREE.MeshStandardMaterial({ color: '#2f7a45', roughness: 0.95 }),
  )
  const staemme = new THREE.Mesh(
    teile.staemme,
    new THREE.MeshStandardMaterial({ color: '#6b4a2e', roughness: 1 }),
  )

  const gruppe = new THREE.Group()
  gruppe.name = `${hotel}-park`
  for (const m of [boden, wasser, kronen, staemme]) {
    m.castShadow = true
    m.receiveShadow = true
    gruppe.add(m)
  }
  return aufBauplatz(gruppe, riss)
}
